import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	AdAccountListItem,
	FacebookConnection,
	InstagramIntegration,
	MetaLimits,
	NeedsPageAccessAccount,
	SyncWarning,
	Warning
} from '@brand-lolly/app/_models/brand-instagram-integration.model';
import { AdAccountsService } from '@brand-lolly/app/services/meta-integrations/ad-accounts.service';
import { FacebookConnectionsService } from '@brand-lolly/app/services/meta-integrations/facebook-connections.service';
import { MetaInstagramAccountsService } from '@brand-lolly/app/services/meta-integrations/instagram-accounts.service';
import { SubscriptionsStateService } from '@brand-lolly/app/billing/services/subscriptions-state.service';
import { SideUsageAlias } from '@brand-lolly/app/billing/types/billing.enums';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MetaAdAccountsSectionComponent } from './meta-ad-accounts-section.component';
import { MetaFbConnectionsSectionComponent } from './meta-fb-connections-section.component';
import { MetaIgAccountsSectionComponent } from './meta-ig-accounts-section.component';
import { AnaSettingAnchorDirective } from '@brand-lolly/components/ana-setting-anchor/ana-setting-anchor.directive';

@Component({
	standalone: true,
	selector: 'meta-integrations-block',
	imports: [
		MetaFbConnectionsSectionComponent,
		MetaIgAccountsSectionComponent,
		MetaAdAccountsSectionComponent,
		AnaSettingAnchorDirective
	],
	template: `
		<div class="meta-block">
			<header class="page-header">
				<h2 class="lo-type-heading-3">Meta Integrations</h2>
				<p class="lo-type-text-sm">
					Manage your connected Facebook accounts, Instagram accounts
					and ad accounts.
				</p>
			</header>
			<meta-fb-connections-section
				[connections]="connections"
				[igCountByConnection]="igCountByConnection"
				[syncWarnings]="fbSyncWarnings"
				[loading]="loading"
				(refresh)="loadAll()"
			></meta-fb-connections-section>
			<meta-ig-accounts-section
				[anaSetting]="'integrations.instagram'"
				[accounts]="accounts"
				[limits]="limits"
				[adAccounts]="adAccounts"
				[syncWarnings]="igSyncWarnings"
				[needsPageAccess]="needsPageAccess"
				[hasFacebookConnection]="connections.length > 0"
				[loading]="loading"
				(refresh)="loadAll()"
			></meta-ig-accounts-section>
			<meta-ad-accounts-section
				[accounts]="adAccounts"
				[limits]="limits"
				[syncWarnings]="adSyncWarnings"
				[loading]="loading"
				(refresh)="loadAll()"
			></meta-ad-accounts-section>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;

				.meta-block {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-xl);

					.page-header {
						display: flex;
						flex-direction: column;
						gap: var(--lo-space-gap-xs);

						p {
							color: var(--lo-color-text-secondary);
						}
					}
				}
			}
		`
	]
})
export class MetaIntegrationsBlockComponent implements OnInit, OnDestroy {
	accounts: InstagramIntegration[] = [];
	connections: FacebookConnection[] = [];
	adAccounts: AdAccountListItem[] = [];
	brandWarnings: Warning[] = [];
	limits: MetaLimits | null = null;
	igCountByConnection: Record<number, number> = {};
	needsPageAccess: NeedsPageAccessAccount[] = [];
	igSyncWarnings: SyncWarning[] = [];
	fbSyncWarnings: SyncWarning[] = [];
	adSyncWarnings: SyncWarning[] = [];
	loading = true;

	private destroy$ = new Subject<void>();

	constructor(
		private igAccounts: MetaInstagramAccountsService,
		private fbConnections: FacebookConnectionsService,
		private adAccountsService: AdAccountsService,
		private subscriptionsState: SubscriptionsStateService
	) {}

	ngOnInit(): void {
		this.loadAll();
	}

	loadAll(): void {
		this.loading = true;
		forkJoin({
			ig: this.igAccounts.list(),
			fb: this.fbConnections.list(),
			ad: this.adAccountsService.list(),
			warnings: this.igAccounts.getWarnings()
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ ig, fb, ad, warnings }) => {
					this.accounts = ig?.data ?? [];
					this.limits = ig?.limits ?? null;
					this.applySideInstagramCap();
					this.igSyncWarnings = ig?.sync_warnings ?? [];
					this.connections = fb?.data ?? [];
					this.needsPageAccess = this.connections.flatMap(
						connection => connection.needs_page_access ?? []
					);
					this.fbSyncWarnings = fb?.sync_warnings ?? [];
					this.adAccounts = ad?.data ?? [];
					this.adSyncWarnings = ad?.sync_warnings ?? [];
					this.brandWarnings = warnings ?? [];
					this.computeIgCountByConnection();
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	/**
	 * The billing side-plan cap is the source of truth for the Instagram limit.
	 * Override the Meta-provided `plan_max` (and recompute `remaining_slots`)
	 * with the side cap, keeping the live `currently_consumed` from Meta. When
	 * the side cap is `null` (not loaded / unlimited), leave Meta's values as-is.
	 */
	private applySideInstagramCap(): void {
		if (!this.limits) {
			return;
		}
		const sideCap = this.subscriptionsState.getSideEntitlementCap(
			SideUsageAlias.INSTAGRAM_ACCOUNTS
		);
		if (sideCap == null) {
			return;
		}
		this.limits.instagram.plan_max = sideCap;
		this.limits.instagram.remaining_slots = Math.max(
			0,
			sideCap - this.limits.instagram.currently_consumed
		);
	}

	private computeIgCountByConnection(): void {
		const map: Record<number, number> = {};
		for (const acc of this.accounts) {
			if (acc.fb_connection_id !== null) {
				map[acc.fb_connection_id] =
					(map[acc.fb_connection_id] ?? 0) + 1;
			}
		}
		this.igCountByConnection = map;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
